import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/products', label: '//Products' },
    { path: '/case-studies', label: '//Projects' },
    { path: '/resources', label: '//Resources' },
    { path: '/about', label: '//Company' },
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-charcoal/95 backdrop-blur-md border-b border-border-dark' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="logo-link"
          >
            <div className="w-10 h-10 border border-gold/50 flex items-center justify-center group-hover:border-gold transition-colors">
              <span className="font-mono text-gold text-sm font-bold">SC</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-mono text-stone text-sm tracking-wider">STANDARD</span>
              <span className="font-mono text-gold text-sm tracking-wider ml-1">COMPOSITES</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.replace('/', '')}`}
                className={`text-mono text-xs tracking-wide transition-colors duration-200 ${
                  location.pathname === link.path || location.pathname.startsWith(link.path + '/')
                    ? 'text-gold'
                    : 'text-stone/50 hover:text-stone'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-quote-btn"
              className="btn btn-outline text-xs px-4 py-2"
            >
              Contact_Us
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-stone"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden bg-charcoal border-t border-border-dark"
          data-testid="mobile-menu"
        >
          <nav className="px-6 py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.path.replace('/', '')}`}
                className={`flex items-center justify-between py-3 border-b border-border-dark text-mono text-sm ${
                  location.pathname === link.path ? 'text-gold' : 'text-stone/50'
                }`}
              >
                {link.label}
                <ChevronRight size={18} className="text-stone/30" />
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-nav-quote-btn"
              className="btn btn-primary mt-4"
            >
              Contact_Us
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-charcoal border-t border-border-dark" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6 p-3 bg-white/10 rounded inline-block">
              <img 
                src="https://customer-assets.emergentagent.com/job_476a6dd3-db49-4ee1-922f-dcd7c2c3de86/artifacts/ymur0gjs_2.svg"
                alt="Standard Composites"
                className="h-12 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-stone/40 text-xs leading-relaxed font-mono">
              Advanced composite reinforcement materials for infrastructure that demands more than steel can give.
            </p>
          </div>

          {/* Products */}
          <div>
            <h4 className="tech-label mb-6">//Products</h4>
            <ul className="space-y-3">
              <li><Link to="/products/geobar-b" className="text-stone/40 hover:text-gold text-xs font-mono transition-colors" data-testid="footer-geobar-b">GEObar_B [BFRP]</Link></li>
              <li><Link to="/products/geobar-fg" className="text-stone/40 hover:text-gold text-xs font-mono transition-colors" data-testid="footer-geobar-fg">GEObar_FG [GFRP]</Link></li>
              <li><Link to="/products/geofabric" className="text-stone/40 hover:text-gold text-xs font-mono transition-colors" data-testid="footer-geofabric">GEOfabric [Geogrid]</Link></li>
              <li><Link to="/products/chopped-fiber" className="text-stone/40 hover:text-gold text-xs font-mono transition-colors" data-testid="footer-geofiber">GEOfiber [Additive]</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="tech-label mb-6">//Resources</h4>
            <ul className="space-y-3">
              <li><Link to="/case-studies" className="text-stone/40 hover:text-gold text-xs font-mono transition-colors">Case_Studies</Link></li>
              <li><Link to="/resources" className="text-stone/40 hover:text-gold text-xs font-mono transition-colors">Blog_&_Guides</Link></li>
              <li><Link to="/specifications" className="text-stone/40 hover:text-gold text-xs font-mono transition-colors">Specifications</Link></li>
              <li><Link to="/glossary" className="text-stone/40 hover:text-gold text-xs font-mono transition-colors">Glossary</Link></li>
              <li><Link to="/faq" className="text-stone/40 hover:text-gold text-xs font-mono transition-colors">FAQ</Link></li>
            </ul>
          </div>

          {/* Tools */}
          <div>
            <h4 className="tech-label mb-6">//Tools</h4>
            <ul className="space-y-3">
              <li><Link to="/samples" className="text-stone/40 hover:text-gold text-xs font-mono transition-colors">Request_Samples</Link></li>
              <li><Link to="/quote-calculator" className="text-stone/40 hover:text-gold text-xs font-mono transition-colors">Quote_Calculator</Link></li>
              <li><Link to="/comparison-tool" className="text-stone/40 hover:text-gold text-xs font-mono transition-colors">BFRP_vs_Steel</Link></li>
              <li><Link to="/contact" className="text-stone/40 hover:text-gold text-xs font-mono transition-colors">Contact_Us</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-border-dark flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-stone/30 text-xs font-mono">
            © {currentYear} Standard_Composites. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <span className="text-mono text-gold/30 text-xs">ASTM_D7205</span>
            <span className="text-mono text-gold/30 text-xs">ACI_440</span>
            <span className="text-mono text-gold/30 text-xs">ISO_9001</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
